import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useResources } from '../hooks/useResourceContext';
import { Button } from '@/components/ui/button';
import { 
  Mail, 
  Phone, 
  Users, 
  Shield, 
  FileText, 
  Video, 
  Link as LinkIcon,
  Menu,
  X,
  PlusCircle,
  FolderPlus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const getIconComponent = (iconName: string) => {
  switch (iconName) {
    case 'phone': return <Phone className="h-5 w-5" />;
    case 'mail': return <Mail className="h-5 w-5" />;
    case 'users': return <Users className="h-5 w-5" />;
    case 'shield': return <Shield className="h-5 w-5" />;
    default: return <FileText className="h-5 w-5" />;
  }
};

const getResourceTypeIcon = (type: string) => {
  switch (type) {
    case 'video': return <Video className="h-5 w-5" />;
    case 'document': return <FileText className="h-5 w-5" />;
    case 'link': return <LinkIcon className="h-5 w-5" />;
    default: return <FileText className="h-5 w-5" />;
  }
};

const getCategoryImage = (categoryId: string) => {
  switch (categoryId) {
    case '1': // VOIP Training
      return "https://placehold.co/300x100/0070ba/ffffff?text=VOIP+Training";
    case '2': // Email Protection
      return "https://placehold.co/300x100/0070ba/ffffff?text=Email+Protection";
    case '3': // Onboarding
      return "https://placehold.co/300x100/0070ba/ffffff?text=Onboarding";
    case '4': // Security
      return "https://placehold.co/300x100/0070ba/ffffff?text=Security";
    default:
      return "https://placehold.co/300x100/0070ba/ffffff?text=Resources";
  }
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { categories } = useResources();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isAuthenticated = true;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="min-h-screen bg-brand-gray">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="mr-2 lg:hidden"
            >
              {isSidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/lovable-uploads/5646859d-698e-4e59-a9b1-a032fcf2e9c9.png" 
                alt="DOMTEK Training Resource Center" 
                className="h-12 w-auto mr-2"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }} 
              />
              <h1 className="text-xl font-bold text-brand-blue ml-2">Training Resource Center</h1>
            </div>
          </div>
          <div>
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="text-brand-blue border-brand-blue hover:bg-brand-blue hover:text-white"
                  >
                    <PlusCircle className="mr-2 h-4 w-4" /> Add New
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => navigate('/add-resource')}>
                    <FileText className="mr-2 h-4 w-4" />
                    <span>Add Resource</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/add-category')}>
                    <FolderPlus className="mr-2 h-4 w-4" />
                    <span>Add Category</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 flex flex-col lg:flex-row">
        <aside 
          className={`
            ${isSidebarOpen ? 'block' : 'hidden'} 
            lg:block 
            w-full lg:w-64 
            bg-white 
            shadow-md 
            rounded-lg 
            p-6 
            mr-0 
            lg:mr-8 
            mb-8 
            lg:mb-0
            flex-shrink-0
          `}
        >
          <h2 className="text-xl font-semibold mb-6 text-gray-800">Categories</h2>
          <nav>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => {
                    navigate('/');
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 rounded-md hover:bg-gray-100 transition-colors 
                    ${location.pathname === '/' ? 'bg-gray-100 text-brand-blue font-semibold' : 'text-gray-700'}`}
                >
                  <FileText className="mr-3 h-5 w-5" />
                  <span>All Resources</span>
                </button>
              </li>
              {categories.map(category => (
                <li key={category.id}>
                  <button 
                    onClick={() => {
                      navigate(`/category/${category.id}`);
                      setIsSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 rounded-md hover:bg-gray-100 transition-colors 
                      ${location.pathname === `/category/${category.id}` ? 'bg-gray-100 text-brand-blue font-semibold' : 'text-gray-700'}`}
                  >
                    {getIconComponent(category.icon)}
                    <span className="ml-3">{category.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="flex-grow">
          {children}
        </main>
      </div>
    </div>
  );
};

export { Layout, getResourceTypeIcon };
